import { useMemo } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import type { HedgeStatusValue, TradeActionValue, VolumeMatchingItemDto } from '@/api/types/consolidatedExposure';

// Mirrors nop-renderers.js HEDGE_STATUS / TRADE_ACTION maps (numeric enums).
const HEDGE: Record<HedgeStatusValue, { bg: string; label: string }> = {
  0: { bg: 'bg-success-transparent', label: 'Balanced' },
  1: { bg: 'bg-warning-transparent', label: 'Under-hedged' },
  2: { bg: 'bg-primary-transparent', label: 'Over-hedged' },
  3: { bg: 'bg-danger-transparent', label: 'Wrong Direction' },
};
const ACTION: Record<TradeActionValue, { bg: string; label: string }> = {
  0: { bg: 'bg-dark-transparent', label: 'NO ACTION' },
  1: { bg: 'bg-success-transparent', label: 'BUY' },
  2: { bg: 'bg-danger-transparent', label: 'SELL' },
};

function num(v: number | null | undefined): string {
  return v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2);
}

export function VolumeMatchingTable({ items, card = true }: { items: VolumeMatchingItemDto[]; card?: boolean }) {
  const rows = useMemo(() => [...items].sort((a, b) => Number(b.isBreach) - Number(a.isBreach)), [items]);

  const columns: DataGridColumn<VolumeMatchingItemDto>[] = [
    { key: 'symbol', title: 'Instrument', className: 'text-start', value: (r) => r.symbol },
    { key: 'clientNetVolume', title: 'Client Net Volume', className: 'text-end', value: (r) => r.clientNetVolume, cell: (r) => num(r.clientNetVolume) },
    { key: 'lpNetVolume', title: 'LP Net Volume', className: 'text-end', value: (r) => r.lpNetVolume, cell: (r) => num(r.lpNetVolume) },
    {
      key: 'hedgeStatus',
      title: 'Hedge Status',
      className: 'text-center',
      value: (r) => HEDGE[r.hedgeStatus]?.label ?? '',
      cell: (r) => {
        const h = HEDGE[r.hedgeStatus] ?? HEDGE[0];
        return <span className={`badge ${h.bg}`}>{h.label}</span>;
      },
    },
    {
      key: 'tradeToBalance',
      title: 'Trade to Balance',
      className: 'text-end',
      value: (r) => r.tradeToBalance,
      cell: (r) => {
        const a = ACTION[r.action] ?? ACTION[0];
        return (
          <>
            <span>{num(r.tradeToBalance)}</span>
            <span className={`badge ${a.bg} ms-1`}>{a.label}</span>
          </>
        );
      },
    },
  ];

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => r.symbol}
      pageSize={5}
      card={card}
      includeExcel
      excelFileName="volume-matching"
      rowClassName={(r) => (r.isBreach ? 'threshold-breach' : undefined)}
      emptyMessage="Waiting for live data…"
    />
  );
}
